import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import { openApi } from "../../services/api";

// Import landing page components
import NavigationMenu from "../../components/NavigationMenu";
import HeroSection from "../../components/HeroSection";
import FeaturesSection from "../../components/FeaturesSection";
import TestimonialsSection from "../../components/TestimonialsSection";
import CTASection from "../../components/CTASection";
import FooterSection from "../../components/FooterSection";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    padding: theme.spacing(4),
    textAlign: "center",
  },
  errorText: {
    marginTop: theme.spacing(2),
    color: theme.palette.error.main,
  },
}));

const LandingPage = () => {
  const classes = useStyles();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        console.log('Fetching landing page from:', `${process.env.REACT_APP_BACKEND_URL}/api/landing-page/published`);
        const { data } = await openApi.get("/api/landing-page/published");
        console.log('Landing page data received:', data);
        setContent(data);
        
        // Set page title and meta tags
        if (data.metadata?.title) {
          document.title = data.metadata.title;
        }
        if (data.metadata?.description) {
          let metaDescription = document.querySelector('meta[name="description"]');
          if (!metaDescription) {
            metaDescription = document.createElement('meta');
            metaDescription.name = "description";
            document.head.appendChild(metaDescription);
          }
          metaDescription.content = data.metadata.description;
        }
        if (data.metadata?.ogImage) {
          let metaOgImage = document.querySelector('meta[property="og:image"]');
          if (!metaOgImage) {
            metaOgImage = document.createElement('meta');
            metaOgImage.setAttribute('property', 'og:image');
            document.head.appendChild(metaOgImage);
          }
          metaOgImage.content = data.metadata.ogImage;
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Error loading landing page content:", err);
        setError("Error al cargar el contenido de la página");
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !content) {
    return (
      <Box className={classes.errorContainer}>
        <Typography variant="h5" className={classes.errorText}>
          {error || "No se pudo cargar el contenido"}
        </Typography>
      </Box>
    );
  }

  // Sort sections by order
  const sortedSections = [...content.sections]
    .filter((section) => section.isActive)
    .sort((a, b) => a.order - b.order);

  // Render section based on type
  const renderSection = (section) => {
    const { type, data } = section;

    switch (type) {
      case "navigation":
        return <NavigationMenu key={section.id} {...data} />;
      case "hero":
        return <HeroSection key={section.id} {...data} />;
      case "features":
        return <FeaturesSection key={section.id} {...data} />;
      case "testimonials":
        return <TestimonialsSection key={section.id} {...data} />;
      case "cta":
        return <CTASection key={section.id} {...data} />;
      case "footer":
        return <FooterSection key={section.id} {...data} />;
      default:
        return null;
    }
  };

  return (
    <div className={classes.root}>
      {sortedSections.map((section) => renderSection(section))}
    </div>
  );
};

export default LandingPage;
